import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Alert,
  Row,
  Col,
  Spinner,
  ListGroup,
  Form,
  InputGroup,
  Badge,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaCheck,
  FaVolumeUp,
  FaTrash,
  FaServer,
  FaSync,
  FaEdit,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import AudioRecorder from "../components/audio/AudioRecorder";
import {
  getEstimate,
  updateEstimateWithDoors,
} from "../services/estimateService";
import { getCustomer } from "../services/customerService";
import {
  getAudioRecordings,
  transcribeAudio,
  processAudioWithAI,
  deleteAudio,
} from "../services/audioService";
import { createBid, addDoorsToBid } from "../services/bidService";
import api from "../services/api";
import "./EstimateInProgress.css";

const EstimateInProgress = () => {
  const { estimateId } = useParams();
  const navigate = useNavigate();

  // State management
  const [estimate, setEstimate] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [doors, setDoors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionError, setConnectionError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState("");
  const [savingDoors, setSavingDoors] = useState(false);
  const [doorsUpdated, setDoorsUpdated] = useState(false);

  // Edit mode state
  const [editingDoorId, setEditingDoorId] = useState(null);
  const [editingDoorDetails, setEditingDoorDetails] = useState([]);
  const [editingDoorDescription, setEditingDoorDescription] = useState("");

  // Audio playback state
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPlayingId, setCurrentPlayingId] = useState(null);

  /**
   * Get user-friendly status display information for any estimate status
   * @param {Object} estimate - The estimate object
   * @returns {Object} Object with text and variant for display
   */
  const getStatusDisplay = (estimate) => {
    if (!estimate) return { text: 'Unknown', variant: 'secondary' };
    
    const status = estimate.status;
    const statusMap = {
      'pending': { text: 'Pending', variant: 'warning' },
      'scheduled': { text: 'Scheduled', variant: 'info' },
      'in-progress': { text: 'In Progress', variant: 'primary' },
      'draft': { text: 'Draft', variant: 'secondary' },
      'converted': { text: 'Converted to Bid', variant: 'success' },
      'complete': { text: 'Complete', variant: 'success' },
      'cancelled': { text: 'Cancelled', variant: 'danger' },
      null: { text: 'New', variant: 'light' },
      undefined: { text: 'New', variant: 'light' }
    };
    
    return statusMap[status] || { text: status || 'Unknown', variant: 'secondary' };
  };

  /**
   * Determine if estimate allows modifications based on status
   * @param {Object} estimate - The estimate object
   * @returns {boolean} Whether the estimate can be modified
   */
  const isEstimateEditable = (estimate) => {
    if (!estimate) return false;
    
    // Estimates that cannot be modified
    const nonEditableStatuses = ['complete', 'cancelled'];
    return !nonEditableStatuses.includes(estimate.status);
  };

  /**
   * Determine if estimate can be submitted to bid
   * @param {Object} estimate - The estimate object
   * @returns {boolean} Whether the estimate can be submitted to bid
   */
  const canSubmitToBid = (estimate) => {
    if (!estimate) return false;
    
    // Only allow bid submission for certain statuses
    const submittableStatuses = ['pending', 'scheduled', 'in-progress', 'draft', null, undefined];
    return submittableStatuses.includes(estimate.status);
  };

  /**
   * Safely execute API call with error handling
   * @param {Function} apiCall - The API function to call
   * @param {string} errorMessage - Custom error message to display
   * @param {Function} onSuccess - Callback for successful API call
   * @param {boolean} isCritical - If true, sets connectionError state on failure
   * @returns {Promise<any>} - The API call result or null on error
   */
  const safeApiCall = useCallback(
    async (apiCall, errorMessage, onSuccess, isCritical = false) => {
      try {
        const result = await apiCall();
        if (onSuccess && typeof onSuccess === "function") {
          onSuccess(result);
        }
        return result;
      } catch (err) {
        console.error(`${errorMessage}:`, err);

        // Determine if this is a network/connection error
        const isNetworkError =
          err.message === "Network Error" ||
          err.code === "ERR_NETWORK" ||
          !err.response;

        if (isNetworkError && isCritical) {
          setConnectionError(true);
          setError(
            "Unable to connect to the server. Please check that the backend is running and try again."
          );
        } else {
          setError(`${errorMessage}: ${err.message || "Unknown error"}`);
        }

        return null;
      }
    },
    []
  );

  /**
   * Load all estimate data including customer info, recordings, and doors
   * FIXED - Properly handles doors_data field returned by backend
   * Supports all estimate statuses - no restrictions
   */
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setConnectionError(false);


    // Get estimate data - works for any status
    const estimateData = await safeApiCall(
      () => getEstimate(estimateId),
      "Failed to load estimate data",
      (data) => {

        setEstimate(data);
      },
      true // Critical - set connection error if this fails
    );

    // If estimate loaded successfully, get customer data
    if (estimateData && estimateData.customer_id) {

      await safeApiCall(
        () => getCustomer(estimateData.customer_id),
        "Failed to load customer data",
        (data) => {

          setCustomer(data);
        }
      );
    }

    // Load recordings for any estimate status (they might have historical recordings)

    await safeApiCall(
      () => getAudioRecordings(estimateId),
      "Failed to load audio recordings",
      (data) => {

        setRecordings(data || []);
      }
    );

    // FIXED: Load doors from the correct field returned by backend
    if (estimateData) {




      let loadedDoors = [];

      // Backend returns doors in 'doors_data' field (already parsed from JSON)
      if (estimateData.doors_data && Array.isArray(estimateData.doors_data) && estimateData.doors_data.length > 0) {

        loadedDoors = estimateData.doors_data;
      }
      // Fallback: check legacy 'doors' field
      else if (estimateData.doors && Array.isArray(estimateData.doors) && estimateData.doors.length > 0) {

        loadedDoors = estimateData.doors;
      }
      // If doors_count > 0 but no doors found, log for debugging
      else if (estimateData.doors_count && estimateData.doors_count > 0) {
        console.error(`[LOAD DATA] ❌ Expected ${estimateData.doors_count} doors but none found!`);
        console.error(`[LOAD DATA] Available fields:`, Object.keys(estimateData));
        console.error(`[LOAD DATA] doors_data type:`, typeof estimateData.doors_data);
        console.error(`[LOAD DATA] doors_data content:`, estimateData.doors_data);
        
        setError(`This estimate should have ${estimateData.doors_count} door(s) but they could not be loaded. Please check the console for debugging information.`);
        setDoors([]);
      } else {

        setDoors([]);
      }

      // If we found doors, validate and set them
      if (loadedDoors.length > 0) {

        // Validate and normalize door structure
        const validatedDoors = loadedDoors.map((door, index) => {
          const validatedDoor = {
            id: door.id || `loaded-${Date.now()}-${index}`,
            door_number: door.door_number || index + 1,
            description: door.description || `Door #${door.door_number || index + 1}`,
            details: Array.isArray(door.details) ? door.details : (door.details ? [door.details] : [])
          };
          
          // Preserve any additional properties
          Object.keys(door).forEach(key => {
            if (!validatedDoor.hasOwnProperty(key) && door[key] !== undefined) {
              validatedDoor[key] = door[key];
            }
          });
          

          return validatedDoor;
        });
        
        setDoors(validatedDoors);

      }
    } else {
      console.warn(`[LOAD DATA] No estimate data available`);
      setDoors([]);
    }

    setLoading(false);

  }, [estimateId, safeApiCall]);

  
  // Load data when component mounts or estimateId changes
  useEffect(() => {
    if (estimateId) {
      loadData();
    }

    // Cleanup function for audio
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.src = "";
      }
    };
  }, [estimateId, currentAudio, loadData]);

  /**
   * Handle retrying data load after connection error
   */
  const handleRetry = () => {
    loadData();
  };

  /**
   * Enhanced saveDoorsToEstimate function that works for any estimate status
   * Comprehensive error handling and retry logic for production use
   */
  const saveDoorsToEstimate = async (doorsToSave = null) => {
    setSavingDoors(true);
    setError(null);
    
    // Use current doors if no specific doors provided
    const getCurrentDoors = () => {
      if (doorsToSave) {
        return doorsToSave;
      }
      return doors;
    };

    let validDoors = [];
    let retryCount = 0;
    const maxRetries = 5;
    
    try {
      // Get the most current doors data
      let doorsData = getCurrentDoors();
      

      // Comprehensive validation
      if (!doorsData || !Array.isArray(doorsData) || doorsData.length === 0) {
        const errorMsg = !doorsData
          ? "No door data available - state may not be properly initialized"
          : !Array.isArray(doorsData)
          ? `Invalid door data format - expected array, got ${typeof doorsData}`
          : "No doors to save - please add doors first by recording audio";

        console.error(`[SAVE DOORS] Validation failed:`, {
          doorsData: doorsData,
          isArray: Array.isArray(doorsData),
          length: doorsData?.length,
          type: typeof doorsData
        });

        setError(errorMsg);
        setSavingDoors(false);
        return null;
      }

      // Validate individual door objects
      const invalidDoors = doorsData.filter((door, index) => {
        if (!door || typeof door !== 'object') {
          console.warn(`[SAVE DOORS] Door at index ${index} is not a valid object:`, door);
          return true;
        }
        return false;
      });

      if (invalidDoors.length > 0) {
        setError(`Found ${invalidDoors.length} invalid door objects. Please refresh and try again.`);
        setSavingDoors(false);
        return null;
      }

      // Validate and normalize each door object
      validDoors = doorsData.map((door, index) => {
        // Generate consistent ID if missing
        const doorId = door.id || `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Ensure door_number is valid
        let doorNumber = door.door_number;
        if (!doorNumber || isNaN(parseInt(doorNumber))) {
          doorNumber = index + 1;
          console.warn(`[SAVE DOORS] Door at index ${index} missing/invalid door_number, assigned: ${doorNumber}`);
        }

        // Ensure description exists
        let description = door.description;
        if (!description || description.trim() === '') {
          description = `Door #${doorNumber}`;
          console.warn(`[SAVE DOORS] Door at index ${index} missing description, assigned: ${description}`);
        }

        // Ensure details is an array and filter out empty strings
        let details = door.details;
        if (!Array.isArray(details)) {
          details = details ? [String(details)] : [];
          console.warn(`[SAVE DOORS] Door at index ${index} had non-array details, converted to array`);
        }
        details = details.filter(detail => detail && String(detail).trim() !== '');

        const validatedDoor = {
          id: doorId,
          door_number: parseInt(doorNumber),
          description: description.trim(),
          details: details
        };


        return validatedDoor;
      });

      // Main save operation with retry logic
      const attemptSave = async (attempt) => {

        setSubmissionProgress(`Saving doors to estimate... (attempt ${attempt + 1})`);

        // Create the payload structure
        const requestPayload = {
          doors: validDoors,
          metadata: {
            timestamp: new Date().toISOString(),
            attempt: attempt + 1,
            clientId: `client-${Date.now()}`,
            doorsCount: validDoors.length
          }
        };


        // Dynamic timeout based on attempt and request size
        const baseTimeout = 30000; // 30 seconds base
        const requestSize = JSON.stringify(requestPayload).length;
        let timeout = baseTimeout;
        
        // Increase timeout for large requests
        if (requestSize > 50000) {
          timeout = Math.max(timeout, 45000);
        }
        
        // Decrease timeout for retry attempts (fail faster on retries)
        if (attempt > 0) {
          timeout = Math.max(15000, timeout - (attempt * 5000));
        }


        // Make the API call with proper headers
        const response = await api.put(
          `/estimates/${estimateId}/doors`,
          requestPayload,
          {
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "Cache-Control": "no-cache"
            },
            timeout: timeout,
            validateStatus: function (status) {
              return status >= 200 && status < 300;
            },
            metadata: {
              operation: 'save-doors',
              doorCount: validDoors.length,
              estimateId: estimateId,
              attempt: attempt + 1,
              timestamp: new Date().toISOString()
            }
          }
        );

        return response;
      };

      // Main retry loop with exponential backoff
      let lastError = null;
      let response = null;

      for (retryCount = 0; retryCount <= maxRetries; retryCount++) {
        try {
          response = await attemptSave(retryCount);
          
          // If we get here, the request succeeded

          break;
          
        } catch (err) {
          lastError = err;
          console.error(`[SAVE DOORS] Attempt ${retryCount + 1} failed:`, err.message);

          // Determine if error is retryable
          const isRetryableError = (
            err.code === 'ERR_NETWORK' ||
            err.code === 'ECONNABORTED' ||
            err.message === 'Network Error' ||
            err.message.includes('timeout') ||
            err.message.includes('ECONNRESET') ||
            err.message.includes('ECONNREFUSED') ||
            (err.response && err.response.status >= 500) ||
            (err.response && err.response.status === 502) || // Bad Gateway
            (err.response && err.response.status === 503) || // Service Unavailable
            (err.response && err.response.status === 504) || // Gateway Timeout
            !err.response // No response received
          );

          // Don't retry CORS errors
          const isCorsError = err.message.includes('CORS') || 
                            err.message.includes('Access-Control') ||
                            err.message.includes('preflight');

          if (isCorsError) {
            console.error(`[SAVE DOORS] CORS error detected - not retrying:`, err.message);
            setError(`CORS configuration issue: The server is blocking this request. Please check the backend CORS settings.`);
            break;
          }

          // Don't retry on client errors (4xx) except for specific cases
          if (err.response && err.response.status >= 400 && err.response.status < 500) {
            if (err.response.status !== 408 && err.response.status !== 429) { // Timeout and Rate Limit are retryable

              break;
            }
          }

          if (!isRetryableError || retryCount >= maxRetries) {

            break;
          }

          // Calculate delay with exponential backoff and jitter
          const baseDelay = Math.min(1000 * Math.pow(2, retryCount), 10000); // Cap at 10 seconds
          const jitter = Math.random() * 1000; // Add up to 1 second of jitter
          const delay = baseDelay + jitter;


          setSubmissionProgress(`Retry ${retryCount + 1}/${maxRetries} in ${Math.round(delay/1000)}s...`);

          // Check online status before retry
          if (!navigator.onLine) {
            console.error(`[SAVE DOORS] Browser is offline, extending delay`);
            await new Promise(resolve => setTimeout(resolve, delay * 2));
          } else {
            await new Promise(resolve => setTimeout(resolve, delay));
          }

          // Update progress for next attempt
          setSubmissionProgress(`Retrying save operation (${retryCount + 2}/${maxRetries + 1})...`);
        }
      }

      // Check if all attempts failed
      if (!response) {
        throw lastError || new Error('All retry attempts failed');
      }

      // Validate response and handle success
      if (response.status >= 200 && response.status < 300 && response.data) {
        const responseData = response.data;

        // Check if backend explicitly indicates failure
        if (responseData.success === false) {
          const errorMsg = responseData.error || responseData.message || "Server indicated save failed";
          console.error(`[SAVE DOORS] Backend returned failure response:`, responseData);
          setError(`Failed to save doors: ${errorMsg}`);
          setSubmissionProgress("");
          return null;
        }

        // Update local state if we're not passing explicit doors
        if (!doorsToSave) {

          setDoors(prevDoors => {
            if (JSON.stringify(prevDoors) !== JSON.stringify(validDoors)) {

              return validDoors;
            }
            return prevDoors;
          });
        }

        setDoorsUpdated(true);
        setSubmissionProgress("Doors saved successfully!");

        // Reset success indicator and progress after delay
        setTimeout(() => {
          setDoorsUpdated(false);
          setSubmissionProgress("");
        }, 3000);


        return {
          ...responseData,
          doors: validDoors,
          doorCount: validDoors.length,
          attemptsUsed: retryCount + 1
        };
      } else {
        // Handle unexpected response structure
        const errorMsg = response.data?.error || 
                        response.data?.message || 
                        `Unexpected response status: ${response.status}`;
        console.error(`[SAVE DOORS] Unexpected response:`, {
          status: response.status,
          data: response.data,
          headers: response.headers
        });
        setError(`Failed to save doors: ${errorMsg}`);
        setSubmissionProgress("");
        return null;
      }

    } catch (err) {
      // Comprehensive error handling with user guidance
      console.error("[SAVE DOORS] Save operation failed after all retries:", err);

      let userFriendlyError;
      let errorCode = 'UNKNOWN_ERROR';

      // Handle different types of errors
      if (err.response) {
        const status = err.response.status;
        const errorData = err.response.data;

        console.error("[SAVE DOORS] Server error response:", {
          status: status,
          data: errorData,
          headers: err.response.headers,
          attemptsUsed: retryCount + 1
        });

        if (status === 400) {
          userFriendlyError = errorData?.error || errorData?.message || "Invalid request data";
          errorCode = 'INVALID_DATA';
        } else if (status === 401) {
          userFriendlyError = "Authentication required";
          errorCode = 'AUTH_REQUIRED';
        } else if (status === 404) {
          userFriendlyError = `Estimate not found (ID: ${estimateId})`;
          errorCode = 'ESTIMATE_NOT_FOUND';
        } else if (status === 422) {
          userFriendlyError = "Data validation failed on server";
          errorCode = 'VALIDATION_FAILED';
        } else if (status >= 500) {
          userFriendlyError = `Server error (${status})`;
          errorCode = 'SERVER_ERROR';
        } else {
          userFriendlyError = errorData?.error || errorData?.message || `Server error (${status})`;
          errorCode = 'HTTP_ERROR';
        }
      } else if (err.request) {
        // Network issues
        console.error("[SAVE DOORS] No response received:", err.request);

        if (err.message.includes('CORS') || err.message.includes('Access-Control') || err.message.includes('preflight')) {
          userFriendlyError = "CORS policy violation";
          errorCode = 'CORS_ERROR';
        } else if (api.defaults?.baseURL?.includes("ngrok")) {
          userFriendlyError = "Ngrok tunnel connection issue";
          errorCode = 'NGROK_TUNNEL_ERROR';
        } else {
          userFriendlyError = "Network connection problem";
          errorCode = 'NETWORK_ERROR';
        }
      } else if (err.code === "ECONNABORTED") {
        userFriendlyError = "Request timed out";
        errorCode = 'REQUEST_TIMEOUT';
      } else {
        console.error("[SAVE DOORS] Request setup error:", err.message);
        userFriendlyError = err.message || "Unknown error occurred";
        errorCode = 'SETUP_ERROR';
      }

      // Create comprehensive error message
      const errorMessage = `Unable to save doors: ${userFriendlyError}`;

      setError(errorMessage);
      setSubmissionProgress("");

      // Show detailed debug information in development
      if (process.env.NODE_ENV === "development") {
        console.group("SAVE DOORS TROUBLESHOOTING");









        console.groupEnd();
      }

      return null;
    } finally {
      setSavingDoors(false);
    }
  };

  /**
   * Handle successful upload of an audio recording
   * Works for any estimate status as long as estimate is editable
   */
  const handleAudioUploaded = async (recording) => {
    // Check if estimate allows modifications
    if (!isEstimateEditable(estimate)) {
      setError("This estimate cannot be modified due to its current status.");
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      // First add the recording to the list
      setRecordings((prev) => [...prev, recording]);

      // Step 1: Transcribe the audio
      const transcribeResponse = await safeApiCall(
        () => transcribeAudio(recording.id),
        "Error transcribing audio"
      );

      if (!transcribeResponse) {
        setIsProcessing(false);
        return;
      }

      // Step 2: Process with AI
      const processResponse = await safeApiCall(
        () => processAudioWithAI(recording.id),
        "Error processing audio with AI"
      );

      if (!processResponse) {
        setIsProcessing(false);
        return;
      }

      // Step 3: Add doors from processing
      if (processResponse.doors && processResponse.doors.length > 0) {
        // Find the highest door number in the existing doors list
        let highestDoorNumber = 0;
        doors.forEach((door) => {
          const doorNum = parseInt(door.door_number, 10);
          if (!isNaN(doorNum) && doorNum > highestDoorNumber) {
            highestDoorNumber = doorNum;
          }
        });

        // Process and renumber the new doors
        const processedDoors = processResponse.doors.map((door, index) => {
          const newDoorNumber = highestDoorNumber + index + 1;

          if (!door.id) {
            door.id = `temp-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`;
          }

          // Extract location from details
          let location = "";
          if (door.details && door.details.length > 0) {
            for (const detail of door.details) {
              if (detail.startsWith("Location:")) {
                location = detail.split("Location:")[1].trim();
                break;
              }
            }
          }

          // Update door details that reference the old door number
          const updatedDetails = door.details.map((detail) => {
            if (detail.includes(`Door #${door.door_number}`)) {
              return detail.replace(
                `Door #${door.door_number}`,
                `Door #${newDoorNumber}`
              );
            }
            return detail;
          });

          return {
            ...door,
            door_number: newDoorNumber,
            details: updatedDetails,
            description: location
              ? `Door #${newDoorNumber} (${location})`
              : `Door #${newDoorNumber}`,
          };
        });

        // Create the updated doors array
        const updatedDoorsArray = [...doors, ...processedDoors];

        // Update the doors state
        setDoors(updatedDoorsArray);

        // Save the updated doors directly
        await saveDoorsToEstimate(updatedDoorsArray);
      }

      // Refresh recordings to get updated transcripts
      await safeApiCall(
        () => getAudioRecordings(estimateId),
        "Failed to refresh recordings",
        (data) => setRecordings(data || [])
      );
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handle audio playback with improved error handling and recovery
   * This replaces the existing handlePlayAudio function in EstimateInProgress.js
   */
  const handlePlayAudio = (filePath, recordingId) => {
    // Clear any existing error messages immediately
    setError('');
    
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }

    if (isPlaying && currentPlayingId === recordingId) {
      currentAudio.pause();
      setIsPlaying(false);
      setCurrentPlayingId(null);
      return;
    }

    const backendApiUrl = api.defaults.baseURL;
    const backendRootUrl = backendApiUrl.replace('/api', '');

    const audioPath = filePath.startsWith("http")
      ? filePath
      : `${backendRootUrl}/${filePath.replace(/^\//, "")}`;


    const audio = new Audio(audioPath);
    
    // Track loading state to prevent premature error messages
    let loadingTimeout = null;
    let hasLoaded = false;
    let hasStartedPlaying = false;
    let errorOccurred = false;
    let cleanedUp = false;
    
    // Function to clean up event listeners and timeouts
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
        loadingTimeout = null;
      }
      
      // Remove temporary event listeners
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('progress', handleProgress);
    };
    
    // Handle successful loading states
    const handleCanPlay = () => {

      hasLoaded = true;
      setError(''); // Clear any error that might have been set
    };
    
    const handleCanPlayThrough = () => {

      hasLoaded = true;
      setError(''); // Clear any lingering errors when audio is fully ready
    };
    
    const handleLoadedData = () => {

      hasLoaded = true;
    };
    
    const handleLoadStart = () => {

    };
    
    const handleProgress = () => {

      // Any progress means the file exists and is being loaded
      hasLoaded = true;
    };
    
    // Handle errors with more context and delayed reporting
    const handleError = (e) => {

      errorOccurred = true;
      
      // Check the specific error type
      const error = audio.error;
      
      if (error) {


        // Only show error after a delay, and only if audio hasn't loaded or started playing
        setTimeout(() => {
          if (!hasLoaded && !hasStartedPlaying && errorOccurred && !cleanedUp) {
            let errorMessage = 'Unable to play audio. ';
            
            switch (error.code) {
              case 1: // MEDIA_ERR_ABORTED
                errorMessage += 'Playback was aborted.';
                break;
              case 2: // MEDIA_ERR_NETWORK
                errorMessage += 'Network error while loading audio.';
                break;
              case 3: // MEDIA_ERR_DECODE
                errorMessage += 'Audio file is corrupted or unsupported format.';
                break;
              case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
                errorMessage += 'Audio format not supported by your browser.';
                break;
              default:
                errorMessage += `File not found at ${audioPath} or network issue.`;
            }
            

            setError(errorMessage);
            setIsPlaying(false);
            setCurrentPlayingId(null);
          }
        }, 2000); // Wait 2 seconds before showing error
      }
    };
    
    // Add event listeners for better tracking
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('progress', handleProgress);
    
    // Add the permanent event listeners that should stay
    audio.addEventListener("ended", () => {

      setIsPlaying(false);
      setCurrentPlayingId(null);
      cleanup();
    });
    
    audio.addEventListener("play", () => {

      hasStartedPlaying = true;
      hasLoaded = true;
      setIsPlaying(true);
      setCurrentAudio(audio);
      setCurrentPlayingId(recordingId);
      setError(''); // Clear any errors when playback starts
    });
    
    audio.addEventListener("pause", () => {

      setIsPlaying(false);
    });
    
    // Set a loading timeout - if audio hasn't loaded after 15 seconds, show error
    loadingTimeout = setTimeout(() => {
      if (!hasLoaded && !hasStartedPlaying && !cleanedUp) {

        cleanup();
        
        // Only show error if we haven't already shown one and audio isn't playing
        if (!errorOccurred && !hasStartedPlaying) {
          setError(`Audio is taking too long to load. The file may be unavailable or your connection is slow.`);
          setIsPlaying(false);
          setCurrentPlayingId(null);
        }
      }
    }, 15000); // 15 second timeout
    
    // Set the source - this will trigger loading
    audio.src = audioPath;
    
    // Attempt to play the audio
    const playPromise = audio.play();
    
    if (playPromise !== undefined) {
      playPromise
        .then(() => {

          hasStartedPlaying = true;
          hasLoaded = true;
          setError(''); // Clear any errors
          setIsPlaying(true);
          setCurrentAudio(audio);
          setCurrentPlayingId(recordingId);
          
          // Clean up temporary listeners after successful play
          setTimeout(cleanup, 1000);
        })
        .catch((error) => {

          // Handle specific play errors
          if (error.name === 'NotAllowedError') {
            // User needs to interact with the page first
            setError('Please click the play button to start audio playback.');
            setIsPlaying(false);
            setCurrentPlayingId(null);
            cleanup();
          } else if (error.name === 'NotSupportedError') {
            setError('Audio format not supported by your browser.');
            setIsPlaying(false);
            setCurrentPlayingId(null);
            cleanup();
          } else if (error.name === 'AbortError') {
            // Play was aborted, which is often normal (user clicked something else)

            setIsPlaying(false);
            setCurrentPlayingId(null);
            cleanup();
          } else {
            // For other errors, wait to see if the audio loads anyway

            // Set a timeout to clear play error if audio starts working
            setTimeout(() => {
              if (!hasStartedPlaying && !hasLoaded && !cleanedUp) {
                setError(`Failed to play audio: ${error.message || "Unknown playback error"}`);
                setIsPlaying(false);
                setCurrentPlayingId(null);
                cleanup();
              }
            }, 3000);
          }
        });
    } else {
      // Older browser support

    }
  };  

  /**
   * Handle deleting a recording - only if estimate is editable
   */
  const handleDeleteRecording = async (recordingId) => {
    if (!isEstimateEditable(estimate)) {
      setError("This estimate cannot be modified due to its current status.");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this recording?"
    );
    if (!confirmed) return;

    try {
      const result = await safeApiCall(
        () => deleteAudio(recordingId),
        "Failed to delete recording"
      );

      if (result) {
        setRecordings((prev) => prev.filter((rec) => rec.id !== recordingId));
      }
    } catch (err) {
      // Error already handled by safeApiCall
    }
  };

  /**
   * Handle editing a door - only if estimate is editable
   */
  const handleEditDoor = (door) => {
    if (!isEstimateEditable(estimate)) {
      setError("This estimate cannot be modified due to its current status.");
      return;
    }

    setEditingDoorId(door.id);
    setEditingDoorDetails([...door.details]);
    setEditingDoorDescription(door.description);
  };

  /**
   * Handle updating a door detail during edit
   */
  const handleUpdateDoorDetail = (index, value) => {
    const updatedDetails = [...editingDoorDetails];
    updatedDetails[index] = value;
    setEditingDoorDetails(updatedDetails);
  };

  /**
   * Add a new blank detail to a door being edited
   */
  const handleAddDoorDetail = () => {
    setEditingDoorDetails([...editingDoorDetails, ""]);
  };

  /**
   * Remove a detail from a door being edited
   */
  const handleRemoveDoorDetail = (index) => {
    const updatedDetails = [...editingDoorDetails];
    updatedDetails.splice(index, 1);
    setEditingDoorDetails(updatedDetails);
  };

  /**
   * Save edited door changes
   */
  const handleSaveDoorEdit = async () => {
    try {

      if (!editingDoorId) {
        setError("No door selected for editing");
        return;
      }
      
      if (!editingDoorDescription.trim()) {
        setError("Door description cannot be empty");
        return;
      }
      

      // Find and update the door in the doors array
      const updatedDoors = doors.map((door) => {
        if (door.id === editingDoorId) {
          const updatedDoor = {
            ...door,
            description: editingDoorDescription.trim(),
            details: editingDoorDetails
              .map(detail => detail.trim())
              .filter(detail => detail !== ""),
          };

          return updatedDoor;
        }
        return door;
      });
      
      // Update state immediately
      setDoors(updatedDoors);
      
      // Clear edit mode
      setEditingDoorId(null);
      setEditingDoorDetails([]);
      setEditingDoorDescription("");
      

      // Save the updated doors
      const result = await saveDoorsToEstimate(updatedDoors);
      
      if (result) {

      } else {
        console.warn("[DOOR EDIT] Failed to save door edit - check error messages");
      }
      
    } catch (error) {
      console.error("[DOOR EDIT] Error saving door edit:", error);
      setError(`Failed to save door edits: ${error.message}`);
    }
  };

  /**
   * Reprocess audio recording to extract door information again
   */
  const handleReprocessAudio = async (recordingId) => {
    if (!isEstimateEditable(estimate)) {
      setError("This estimate cannot be modified due to its current status.");
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const recording = recordings.find((rec) => rec.id === recordingId);
      if (!recording) {
        setError("Recording not found");
        setIsProcessing(false);
        return;
      }

      setSubmissionProgress(`Reprocessing recording ${recording.id}...`);
      // Step 1: Transcribe the audio
      setSubmissionProgress("Transcribing audio...");
      const transcribeResponse = await safeApiCall(
        () => transcribeAudio(recordingId),
        "Error transcribing audio"
      );

      if (!transcribeResponse) {
        setIsProcessing(false);
        setSubmissionProgress("");
        return;
      }

      setSubmissionProgress("Extracting door information with AI...");
      // Step 2: Process with AI
      const processResponse = await safeApiCall(
        () => processAudioWithAI(recordingId),
        "Error processing audio with AI"
      );

      if (!processResponse) {
        setIsProcessing(false);
        setSubmissionProgress("");
        return;
      }

      // Step 3: Add doors from processing
      if (processResponse.doors && processResponse.doors.length > 0) {
        setSubmissionProgress(
          `Processing ${processResponse.doors.length} doors...`
        );
        // Find the highest door number in the existing doors list
        let highestDoorNumber = 0;
        doors.forEach((door) => {
          const doorNum = parseInt(door.door_number, 10);
          if (!isNaN(doorNum) && doorNum > highestDoorNumber) {
            highestDoorNumber = doorNum;
          }
        });

        // Process and renumber the new doors
        const processedDoors = processResponse.doors.map((door, index) => {
          const newDoorNumber = highestDoorNumber + index + 1;

          if (!door.id) {
            door.id = `temp-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`;
          }

          // Extract location from details
          let location = "";
          if (door.details && door.details.length > 0) {
            for (const detail of door.details) {
              if (detail.startsWith("Location:")) {
                location = detail.split("Location:")[1].trim();
                break;
              }
            }
          }

          // Update door details that reference the old door number
          const updatedDetails = door.details.map((detail) => {
            if (detail.includes(`Door #${door.door_number}`)) {
              return detail.replace(
                `Door #${door.door_number}`,
                `Door #${newDoorNumber}`
              );
            }
            return detail;
          });

          return {
            ...door,
            door_number: newDoorNumber,
            details: updatedDetails,
            description: location
              ? `Door #${newDoorNumber} (${location})`
              : `Door #${newDoorNumber}`,
          };
        });

        // Create the updated doors array
        const updatedDoorsArray = [...doors, ...processedDoors];

        // Update the doors state
        setDoors(updatedDoorsArray);

        // Save with enhanced error handling
        setSubmissionProgress("Saving doors to estimate...");
        const saveResult = await saveDoorsToEstimate(updatedDoorsArray);

        if (!saveResult) {
          console.error(
            `[REPROCESS AUDIO] Failed to save doors for recording ${recordingId}`
          );
          setSubmissionProgress("Failed to save doors");
          return;
        }
      } else {
        setError("No door information could be extracted from this recording.");
        setSubmissionProgress("");
        setIsProcessing(false);
        return;
      }

      // Refresh recordings
      setSubmissionProgress("Refreshing recording data...");
      await safeApiCall(
        () => getAudioRecordings(estimateId),
        "Failed to refresh recordings",
        (data) => setRecordings(data || [])
      );

      setSubmissionProgress("Reprocessing complete!");
      // Clear progress message after delay
      setTimeout(() => {
        setSubmissionProgress("");
      }, 2000);
    } catch (error) {
      console.error(
        `[REPROCESS AUDIO] Error reprocessing recording ${recordingId}:`,
        error
      );

      let errorMessage = "Failed to reprocess audio recording";

      if (
        error.message?.includes("Network Error") ||
        error.message?.includes("ngrok")
      ) {
        errorMessage =
          "Connection issue while reprocessing. Please check your connection and try again.";
      } else if (error.message?.includes("timeout")) {
        errorMessage =
          "Reprocessing timed out. Please try again - this can happen with slower connections.";
      } else if (error.message?.includes("auth")) {
        errorMessage =
          "Authentication expired. Please refresh the page and try again.";
      }

      setError(`${errorMessage}: ${error.message}`);
      setSubmissionProgress("");
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Cancel door edit
   */
  const handleCancelDoorEdit = () => {
    setEditingDoorId(null);
    setEditingDoorDetails([]);
    setEditingDoorDescription("");
  };

  /**
   * Remove door from estimate - only if estimate is editable
   */
  const handleRemoveDoor = async (doorId) => {
    if (!isEstimateEditable(estimate)) {
      setError("This estimate cannot be modified due to its current status.");
      return;
    }

    try {

      const confirmed = window.confirm(
        "Are you sure you want to remove this door? This action cannot be undone."
      );
      if (!confirmed) {

        return;
      }
      
      const doorToRemove = doors.find(door => door.id === doorId);
      if (!doorToRemove) {
        setError("Door not found - it may have already been removed");
        return;
      }
      

      // Remove the door from the doors array
      const updatedDoors = doors.filter((door) => door.id !== doorId);
      
      // Update state immediately
      setDoors(updatedDoors);
      

      // Save the updated doors
      const result = await saveDoorsToEstimate(updatedDoors);
      
      if (result) {

      } else {
        console.warn("[DOOR REMOVE] Failed to save door removal - check error messages");
      }
      
    } catch (error) {
      console.error("[DOOR REMOVE] Error removing door:", error);
      setError(`Failed to remove door: ${error.message}`);
    }
  };

  /**
   * Handle submitting the estimate to create a bid - only for appropriate statuses
   */
  const handleSubmitToBid = async () => {
    if (!canSubmitToBid(estimate)) {
      setError("This estimate cannot be submitted to bid due to its current status.");
      return;
    }

    if (doors.length === 0) {
      setError("No doors to submit. Please record and process audio first.");
      return;
    }

    setSubmitting(true);
    setError(null);
    setSubmissionProgress("Creating bid...");

    try {
      // Step 1: Create a new bid
      const bidResponse = await safeApiCall(
        () => createBid(estimateId),
        "Failed to create bid"
      );

      if (!bidResponse) {
        setSubmitting(false);
        return;
      }

      const bidId = bidResponse.id;
      setSubmissionProgress(`Adding ${doors.length} doors to bid...`);

      // Step 2: Add all the doors to the bid
      const doorsResponse = await safeApiCall(
        () => addDoorsToBid(bidId, doors),
        "Failed to add doors to bid"
      );

      if (!doorsResponse) {
        setSubmitting(false);
        return;
      }

      setSubmissionProgress("Finalizing bid...");

      // Success!
      setSubmitSuccess(true);
      setSubmissionProgress("Bid created successfully!");

      // Navigate to the bid page after a short delay
      setTimeout(() => {
        navigate(`/bids/${bidId}`);
      }, 2000);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Render connection error with helpful troubleshooting
   */
  const renderConnectionError = () => {
    const isNgrok = api.defaults?.baseURL?.includes("ngrok");

    return (
      <div className="connection-error-container p-5 text-center">
        <FaServer size={48} className="text-danger mb-3" />
        <h3>
          {isNgrok
            ? "Ngrok Tunnel Connection Error"
            : "Server Connection Error"}
        </h3>
        <p>
          Unable to connect to the {isNgrok ? "ngrok tunnel" : "backend server"}
          .
          {isNgrok
            ? " This is common with ngrok free tunnels."
            : " Please ensure the backend server is running."}
        </p>

        {isNgrok ? (
          <div className="alert alert-info">
            <h6 className="alert-heading">
              <FaServer className="me-2" />
              Ngrok Tunnel Troubleshooting
            </h6>
            <ul className="list-unstyled mb-0">
              <li>
                • Check that your ngrok tunnel is still active in terminal
              </li>
              <li>• Ngrok free tunnels have time limits and can be unstable</li>
              <li>
                • Try running: <code>ngrok http 5000</code> to restart tunnel
              </li>
              <li>
                • Consider using a local connection instead:{" "}
                <code>http://localhost:3000</code>
              </li>
              <li>• Free ngrok tunnels may have bandwidth or request limits</li>
            </ul>
          </div>
        ) : (
          <ul className="list-unstyled">
            <li>• The backend server is running at http://127.0.0.1:5000</li>
            <li>• No firewall or network issues are blocking the connection</li>
            <li>• The API endpoint is configured correctly</li>
          </ul>
        )}

        <div className="mt-4">
          <Button variant="primary" onClick={handleRetry} className="me-3">
            <FaSync className="me-2" /> Retry Connection
          </Button>

          {isNgrok && (
            <Button
              variant="outline-info"
              onClick={() =>
                window.open(
                  "https://dashboard.ngrok.com/tunnels/agents",
                  "_blank"
                )
              }
              className="me-3"
            >
              <FaServer className="me-2" /> Check Ngrok Status
            </Button>
          )}

          <Button
            variant="outline-secondary"
            onClick={() => navigate("/estimates")}
          >
            <FaArrowLeft className="me-2" /> Back to Estimates
          </Button>
        </div>

        {process.env.NODE_ENV === "development" && (
          <div className="mt-4 p-3 bg-light rounded">
            <h6>Debug Information:</h6>
            <small className="text-muted">
              <div>Base URL: {api.defaults?.baseURL || "undefined"}</div>
              <div>Online: {navigator.onLine ? "Yes" : "No"}</div>
              <div>
                Connection: {navigator.connection?.effectiveType || "unknown"}
              </div>
              <div>User Agent: {navigator.userAgent.slice(0, 50)}...</div>
            </small>
          </div>
        )}
      </div>
    );
  };

  /**
   * Render error alert with contextual troubleshooting information
   */
  const renderErrorAlert = () => {
    if (!error) return null;

    const isNgrokError =
      error.includes("ngrok") || api.defaults?.baseURL?.includes("ngrok");
    const isNetworkError =
      error.includes("Network Error") || error.includes("connection");
    const isTimeoutError =
      error.includes("timeout") || error.includes("timed out");

    return (
      <Alert
        variant="danger"
        dismissible
        onClose={() => setError(null)}
        className="mb-4"
      >
        <Alert.Heading>
          <FaServer className="me-2" />
          {isNgrokError
            ? "Ngrok Tunnel Issue"
            : isNetworkError
            ? "Connection Problem"
            : "Error"}
        </Alert.Heading>

        <p className="mb-2">{error}</p>

        {error.includes("save") && (
          <div className="mt-3">
            <h6>Current Status:</h6>
            <ul className="mb-0 small">
              <li>Doors in memory: {doors?.length || 0}</li>
              <li>Estimate ID: {estimateId}</li>
              <li>Backend URL: {api.defaults?.baseURL || 'Not set'}</li>
              <li>Connection: {navigator.onLine ? 'Online' : 'Offline'}</li>
            </ul>
          </div>
        )}

        {(isNgrokError || isNetworkError || isTimeoutError) && (
          <div className="mt-3">
            <h6>Troubleshooting Tips:</h6>
            <ul className="mb-0 small">
              {isNgrokError && (
                <>
                  <li>Check if your ngrok tunnel is still active</li>
                  <li>Ngrok free tunnels can be unstable - this is normal</li>
                  <li>Try refreshing the page to reconnect</li>
                  <li>
                    Consider using localhost instead of ngrok for development
                  </li>
                </>
              )}
              {isNetworkError && !isNgrokError && (
                <>
                  <li>Check your internet connection</li>
                  <li>Verify the backend server is running</li>
                  <li>Try refreshing the page</li>
                </>
              )}
              {isTimeoutError && (
                <>
                  <li>
                    The request took too long - this can happen with slow
                    connections
                  </li>
                  <li>Try again in a moment</li>
                  <li>Consider reducing the amount of data being saved</li>
                </>
              )}
            </ul>
          </div>
        )}

        {process.env.NODE_ENV === "development" && (
          <div className="mt-3 p-2 bg-light rounded">
            <small className="text-muted">
              <strong>Debug:</strong>{" "}
              {api.defaults?.baseURL || "No base URL set"}
              {isNgrokError && " (Ngrok tunnel detected)"}
            </small>
          </div>
        )}
      </Alert>
    );
  };

  /**
   * Render submission progress indicator
   */
  const renderSubmissionProgress = () => {
    if (!submissionProgress && !savingDoors && !isProcessing) return null;

    const message =
      submissionProgress ||
      (savingDoors ? "Saving doors..." : "") ||
      (isProcessing ? "Processing..." : "");

    const isNgrok = api.defaults?.baseURL?.includes("ngrok");

    return (
      <div className="submitting-indicator mt-3 d-flex align-items-center">
        <Spinner animation="border" size="sm" className="me-2" />
        <span>{message}</span>
        {isNgrok && message.includes("aving") && (
          <small className="text-muted ms-2">(Ngrok tunnels can be slow)</small>
        )}
      </div>
    );
  };

  // Connection error state
  if (connectionError) {
    return renderConnectionError();
  }

  // Loading state
  if (loading) {
    return (
      <div className="loading-container text-center p-5">
        <Spinner animation="border" />
        <p className="mt-3">Loading estimate data...</p>
      </div>
    );
  }

  // Main component render - supports ALL estimate statuses
  return (
    <>
      {/* Full-width header */}
      <div className="estimate-header-full-width">
        <div className="estimate-header-content">
          <Button
            variant="outline-light"
            onClick={() => navigate("/estimates")}
            className="me-3"
          >
            <FaArrowLeft className="me-2" /> Back to Estimates
          </Button>
          <div>
            <h2 className="mb-0 text-white">Estimate Details</h2>
            {estimate && (
              <div className="mt-1">
                <Badge bg={getStatusDisplay(estimate).variant} className="me-2">
                  Status: {getStatusDisplay(estimate).text}
                </Badge>
                <small className="text-white">EST-{estimate.id}</small>
                {estimate.reference_number && (
                  <small className="text-white ms-2">• Offer #{estimate.reference_number}</small>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contained content */}
      <div className="estimate-in-progress-container">
        {renderErrorAlert()}

      {submitSuccess && (
        <Alert variant="success" className="mb-4">
          Successfully submitted to bid! Redirecting...
        </Alert>
      )}

      {doorsUpdated && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setDoorsUpdated(false)}
          className="mb-4"
        >
          Door information successfully saved!
        </Alert>
      )}

      {/* Customer Information - Always show for any estimate status */}
      <Card className="customer-info-card mb-4">
        <Card.Header>Customer Information</Card.Header>
        <Card.Body>
          {customer ? (
            <Row>
              <Col md={6}>
                <p>
                  <strong>Name:</strong> {customer.name}
                </p>
                <p>
                  <strong>Site:</strong> {estimate?.site_name || "N/A"}
                </p>
                <p>
                  <strong>Site Address:</strong>{" "}
                  {estimate?.site_address || "N/A"}
                </p>
              </Col>
              <Col md={6}>
                <p>
                  <strong>Onsite Contact:</strong>{" "}
                  {estimate?.site_contact_name || "N/A"}
                </p>
                <p>
                  <strong>Phone:</strong> {estimate?.site_phone || "N/A"}
                </p>
                <p>
                  <strong>Lockbox Location:</strong>{" "}
                  {estimate?.site_lockbox_location || "N/A"}
                </p>
              </Col>
            </Row>
          ) : (
            <p className="mb-0">No customer information available.</p>
          )}
        </Card.Body>
      </Card>

      {/* Main functionality - now shown for ALL estimates, with appropriate restrictions */}
      {estimate && (
        <>
          {/* Audio Recording Section - only show if estimate is editable */}
          {isEstimateEditable(estimate) && (
            <Card className="audio-recorder-card mb-4">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <span>Record Door Information</span>
                  <Badge bg={getStatusDisplay(estimate).variant}>
                    {getStatusDisplay(estimate).text}
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body>
                <p className="mb-3">
                  Record audio notes for each door. Speak clearly and include
                  dimensions, specifications, and any special details about the
                  doors.
                </p>
                <AudioRecorder
                  estimateId={estimateId}
                  onAudioUploaded={handleAudioUploaded}
                  onError={(err) =>
                    setError(err.message || "Error with audio recording")
                  }
                />

                {(isProcessing || submissionProgress) &&
                  renderSubmissionProgress()}
              </Card.Body>
            </Card>
          )}

          {/* Saved Recordings Section - always show if recordings exist */}
          {recordings.length > 0 && (
            <Card className="audio-recordings-card mb-4">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <span>Saved Recordings</span>
                  <Badge bg="primary">{recordings.length} recording{recordings.length !== 1 ? 's' : ''}</Badge>
                </div>
              </Card.Header>
              <Card.Body>
                <ListGroup>
                  {recordings.map((recording, index) => (
                    <ListGroup.Item
                      key={recording.id}
                      className="recording-list-item"
                    >
                      <div className="d-flex flex-column flex-md-row justify-content-between w-100">
                        <div className="recording-info flex-grow-1 mb-2 mb-md-0 me-md-3">
                          <strong>Recording {index + 1}</strong>
                          <p className="text-muted small mb-1">
                            {new Date(recording.created_at).toLocaleString()}
                          </p>
                          {recording.transcript && (
                            <div className="transcript-text mt-2 mb-2">
                              <small className="text-wrap">
                                <strong>Transcript:</strong>{" "}
                                {recording.transcript}
                              </small>
                            </div>
                          )}
                        </div>
                        <div className="recording-actions d-flex flex-wrap justify-content-start justify-content-md-end align-items-center mt-2 mt-md-0">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2 mb-2 mb-md-0"
                            onClick={() =>
                              handlePlayAudio(recording.file_path, recording.id)
                            }
                          >
                            <FaVolumeUp className="me-1" />{" "}
                            {isPlaying && currentPlayingId === recording.id
                              ? "Pause"
                              : "Play"}
                          </Button>
                          {isEstimateEditable(estimate) && (
                            <>
                              <Button
                                variant="outline-info"
                                size="sm"
                                className="me-2 mb-2 mb-md-0"
                                onClick={() => handleReprocessAudio(recording.id)}
                                disabled={isProcessing}
                                title="Reprocess this recording to extract door information again"
                              >
                                <FaSync className="me-1" /> Reprocess
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="mb-2 mb-md-0"
                                onClick={() => handleDeleteRecording(recording.id)}
                              >
                                <FaTrash className="me-1" /> Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          )}

          {/* Door Information Section - always show if doors exist */}
          {doors.length > 0 && (
            <Card className="door-information-card mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>Extracted Door Information</span>
                <div>
                  <Badge bg="success" className="me-2">
                    {doors.length} door{doors.length !== 1 ? 's' : ''}
                  </Badge>
                  {isEstimateEditable(estimate) && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={async () => {
                        try {


                          if (!doors || !Array.isArray(doors) || doors.length === 0) {
                            const errorDetails = !doors 
                              ? "doors state is null or undefined" 
                              : !Array.isArray(doors) 
                              ? `doors is not an array (type: ${typeof doors})` 
                              : "no doors in the array";
                            
                            const errorMsg = `Cannot save doors: ${errorDetails}. Please record audio to extract door information first.`;
                            console.error("[MANUAL SAVE] Validation failed:", errorMsg);
                            setError(errorMsg);
                            return;
                          }
                          
                          const invalidDoors = doors.filter((door, index) => {
                            if (!door || typeof door !== 'object') {
                              console.warn(`[MANUAL SAVE] Door at index ${index} is invalid:`, door);
                              return true;
                            }
                            return false;
                          });
                          
                          if (invalidDoors.length > 0) {
                            const errorMsg = `Found ${invalidDoors.length} corrupted door objects. Please refresh the page and try again.`;
                            console.error("[MANUAL SAVE] Data integrity check failed:", invalidDoors);
                            setError(errorMsg);
                            return;
                          }
                          

                          const doorsToSave = JSON.parse(JSON.stringify(doors));

                          const result = await saveDoorsToEstimate(doorsToSave);
                          
                          if (result) {

                          } else {
                            console.warn("[MANUAL SAVE] Save operation returned null - check error messages");
                          }
                          
                        } catch (error) {
                          console.error("[MANUAL SAVE] Unexpected error during save operation:", error);
                          setError(`Manual save failed: ${error.message}`);
                        }
                      }}
                      disabled={savingDoors || editingDoorId !== null || !doors || doors.length === 0}
                      title={!doors || doors.length === 0 ? "No doors to save - record audio first" : "Save all doors to estimate"}
                    >
                      {savingDoors ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-1"
                          />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-1" />
                          Save Doors {doors?.length ? `(${doors.length})` : ''}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-3">
                  <small>
                    You have {doors.length} door{doors.length !== 1 ? "s" : ""}{" "}
                    in this estimate.
                    {isEstimateEditable(estimate) && (
                      <> Record additional audio to add more doors,
                      or use the buttons below to edit or remove existing doors.</>
                    )}
                    {!isEstimateEditable(estimate) && (
                      <> This estimate is {getStatusDisplay(estimate).text.toLowerCase()} and cannot be modified.</>
                    )}
                  </small>
                </p>

                <ListGroup>
                  {doors.map((door, index) => (
                    <ListGroup.Item key={door.id || `door-${door.door_number}-${index}`}>
                      {editingDoorId === door.id ? (
                        <div className="door-edit-container">
                          <Form.Group className="mb-3">
                            <Form.Label>Door Description</Form.Label>
                            <Form.Control
                              type="text"
                              value={editingDoorDescription}
                              onChange={(e) => setEditingDoorDescription(e.target.value)}
                              placeholder="Enter door description..."
                              isInvalid={!editingDoorDescription.trim()}
                            />
                            <Form.Control.Feedback type="invalid">
                              Description is required
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>
                              Door Details 
                              <small className="text-muted ms-2">({editingDoorDetails.length} details)</small>
                            </Form.Label>
                            {editingDoorDetails.map((detail, detailIndex) => (
                              <InputGroup className="mb-2" key={detailIndex}>
                                <Form.Control
                                  type="text"
                                  value={detail}
                                  onChange={(e) => handleUpdateDoorDetail(detailIndex, e.target.value)}
                                  placeholder="Enter detail..."
                                />
                                <Button
                                  variant="outline-danger"
                                  onClick={() => handleRemoveDoorDetail(detailIndex)}
                                  title="Remove this detail"
                                >
                                  <FaTimes />
                                </Button>
                              </InputGroup>
                            ))}
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={handleAddDoorDetail}
                              className="mt-2"
                            >
                              + Add Detail
                            </Button>
                          </Form.Group>

                          <div className="d-flex justify-content-end">
                            <Button
                              variant="secondary"
                              size="sm"
                              className="me-2"
                              onClick={handleCancelDoorEdit}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="success"
                              size="sm"
                              onClick={handleSaveDoorEdit}
                              disabled={!editingDoorDescription.trim()}
                            >
                              <FaSave className="me-1" />
                              Save Changes
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <h5 className="mb-2">
                              {door.description}
                              <small className="text-muted ms-2">
                                (ID: {door.id?.slice(-8) || 'No ID'})
                              </small>
                            </h5>
                            {door.details && door.details.length > 0 ? (
                              <ul className="mb-0">
                                {door.details.map((detail, detailIndex) => (
                                  <li key={detailIndex}>{detail}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-muted mb-0">
                                <em>No details available</em>
                              </p>
                            )}
                          </div>
                          {isEstimateEditable(estimate) && (
                            <div className="flex-shrink-0">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEditDoor(door)}
                                disabled={editingDoorId !== null}
                                title="Edit this door"
                              >
                                <FaEdit className="me-1" /> Edit
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveDoor(door.id)}
                                disabled={editingDoorId !== null}
                                title="Remove this door"
                              >
                                <FaTrash className="me-1" /> Remove
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          )}

          {/* Submit to Bid Section - only show for appropriate statuses */}
          {doors.length > 0 && canSubmitToBid(estimate) && (
            <div className="submit-actions mb-4">
              <Button
                variant="success"
                size="lg"
                onClick={handleSubmitToBid}
                disabled={submitting || submitSuccess || editingDoorId !== null}
                className="submit-button"
              >
                <FaCheck className="me-2" /> Submit to Bid
              </Button>

              {submitting && renderSubmissionProgress()}
            </div>
          )}

          {/* Status-specific messages */}
          {estimate.status === "converted" && (
            <Card className="mb-4">
              <Card.Body>
                <Alert variant="info" className="mb-0">
                  <h5>This estimate has been converted to a bid</h5>
                  <p>You can view and manage the bid details from the bids page.</p>
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/bids?estimateId=${estimateId}`)}
                  >
                    View Associated Bid
                  </Button>
                </Alert>
              </Card.Body>
            </Card>
          )}

          {estimate.status === "complete" && (
            <Card className="mb-4">
              <Card.Body>
                <Alert variant="success" className="mb-0">
                  <h5>This estimate is complete</h5>
                  <p>This estimate has been finished and no further actions are needed.</p>
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate("/estimates")}
                  >
                    Back to Estimates List
                  </Button>
                </Alert>
              </Card.Body>
            </Card>
          )}

          {estimate.status === "cancelled" && (
            <Card className="mb-4">
              <Card.Body>
                <Alert variant="warning" className="mb-0">
                  <h5>This estimate has been cancelled</h5>
                  <p>This estimate is no longer active and cannot be modified.</p>
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate("/estimates")}
                  >
                    Back to Estimates List
                  </Button>
                </Alert>
              </Card.Body>
            </Card>
          )}
        </>
      )}
      </div>
    </>
  );
};

export default EstimateInProgress;